<script lang="ts">
  import { useThrelte } from '@threlte/core'
  import { cameraControls } from '../../stores/camera'
  import {
    BufferGeometry,
    Line,
    LineBasicMaterial,
    Mesh,
    Plane,
    Raycaster,
    Vector2,
    Vector3,
  } from 'three'
  import { makeExtrudedPolygonGeometry } from '../../helpers/three'
  import { sceneMode } from '../../stores/scene-mode'
  import { get } from 'svelte/store'
  import { useKeyboardListener, useMouseEvents } from '../../hooks'
  import {
    forbiddenZoneMaterial,
    forbiddenZoneWireframeMaterial,
    ThreeItemKeys,
    ThreeItemTypes,
  } from '../../constants/three'
  import useCanvasSelection from '../../hooks/canvas-selection'

  const { scene, renderer } = useThrelte()
  const { upsert: selectObject } = useCanvasSelection()

  const CLOSE_THRESHOLD_PX = 14
  const MIN_HEIGHT = 0.01 // meters, avoids a degenerate zero-height zone
  const groundPlane = new Plane(new Vector3(0, 0, 1), 0)
  const previewOutlineMaterial = new LineBasicMaterial({ color: 'red' })

  let points: Vector2[] = []
  let closed = false
  let currentHeight = 0
  let previewOutline: Line<BufferGeometry, LineBasicMaterial> | null = null
  let previewMesh: Mesh<BufferGeometry, typeof forbiddenZoneWireframeMaterial> | null = null
  let heightPlane: Plane | null = null
  let zoneCentroid: Vector2 | null = null

  function getPointerRay(e: MouseEvent): Raycaster | null {
    const cameraControlsValue = get(cameraControls)
    if (!cameraControlsValue?.camera) return null

    const raycaster = new Raycaster()
    const canvasPosition = renderer.domElement.getBoundingClientRect()
    const pointer = new Vector2(
      2 * ((e.clientX - canvasPosition.left) / renderer.domElement.clientWidth) - 1,
      -(2 * ((e.clientY - canvasPosition.top) / renderer.domElement.clientHeight) - 1),
    )
    raycaster.setFromCamera(pointer, cameraControlsValue.camera)
    return raycaster
  }

  function getPointerGroundPoint(e: MouseEvent): Vector3 | null {
    const raycaster = getPointerRay(e)
    if (!raycaster) return null
    return raycaster.ray.intersectPlane(groundPlane, new Vector3())
  }

  function projectToScreen(point: Vector3): { x: number, y: number } | null {
    const cameraControlsValue = get(cameraControls)
    if (!cameraControlsValue?.camera) return null

    const canvasPosition = renderer.domElement.getBoundingClientRect()
    const ndc = point.clone().project(cameraControlsValue.camera)

    return {
      x: canvasPosition.left + ((ndc.x + 1) / 2) * renderer.domElement.clientWidth,
      y: canvasPosition.top + ((1 - ndc.y) / 2) * renderer.domElement.clientHeight,
    }
  }

  function isNearFirstPoint(worldPoint: Vector3): boolean {
    if (points.length < 3) return false

    const firstScreen = projectToScreen(new Vector3(points[0].x, points[0].y, 0))
    const candidateScreen = projectToScreen(worldPoint)
    if (!firstScreen || !candidateScreen) return false

    return Math.hypot(firstScreen.x - candidateScreen.x, firstScreen.y - candidateScreen.y) <= CLOSE_THRESHOLD_PX
  }

  function getCentroid(): Vector2 {
    return points.reduce((acc, p) => acc.add(p), new Vector2()).divideScalar(points.length)
  }

  function toLocalPoints(centroid: Vector2): Vector2[] {
    return points.map(p => new Vector2(p.x - centroid.x, p.y - centroid.y))
  }

  function updatePreviewOutline(withCursor?: Vector3) {
    if (!previewOutline) return

    const linePoints = points.map(p => new Vector3(p.x, p.y, 0))
    if (withCursor) linePoints.push(withCursor)

    previewOutline.geometry.dispose()
    previewOutline.geometry = new BufferGeometry().setFromPoints(linePoints)
  }

  function updatePreviewMesh(height: number) {
    if (!previewMesh || !zoneCentroid) return

    previewMesh.geometry.dispose()
    previewMesh.geometry = makeExtrudedPolygonGeometry(toLocalPoints(zoneCentroid), Math.max(height, MIN_HEIGHT))
  }

  function resetState() {
    previewOutline?.removeFromParent()
    previewOutline = null
    previewMesh?.removeFromParent()
    previewMesh = null
    points = []
    closed = false
    heightPlane = null
    currentHeight = 0
    zoneCentroid = null
  }

  function undoLastPoint() {
    if (closed || !points.length) return

    points = points.slice(0, -1)

    if (!points.length) {
      previewOutline?.removeFromParent()
      previewOutline = null
      return
    }

    updatePreviewOutline()
  }

  function closePolygon() {
    if (!scene) return

    closed = true
    previewOutline?.removeFromParent()
    previewOutline = null

    zoneCentroid = getCentroid()
    previewMesh = new Mesh(makeExtrudedPolygonGeometry(toLocalPoints(zoneCentroid), MIN_HEIGHT), forbiddenZoneWireframeMaterial)
    previewMesh.position.set(zoneCentroid.x, zoneCentroid.y, 0)
    scene.add(previewMesh)

    const cameraControlsValue = get(cameraControls)
    const viewDirection = new Vector3()
    cameraControlsValue?.camera.getWorldDirection(viewDirection)
    const normal = new Vector3(viewDirection.x, viewDirection.y, 0)
    if (normal.lengthSq() < 1e-6) normal.set(1, 0, 0)
    normal.normalize()

    heightPlane = new Plane().setFromNormalAndCoplanarPoint(normal, new Vector3(zoneCentroid.x, zoneCentroid.y, 0))
  }

  function finalizeZone() {
    if (!previewMesh || !zoneCentroid) return

    const finalHeight = Math.max(currentHeight, MIN_HEIGHT)
    const localPoints = toLocalPoints(zoneCentroid)
    previewMesh.geometry.dispose()
    previewMesh.geometry = makeExtrudedPolygonGeometry(localPoints, finalHeight)
    previewMesh.material = forbiddenZoneMaterial
    previewMesh.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.FORBIDDEN_ZONE
    previewMesh.userData[ThreeItemKeys.FORBIDDEN_ZONE_POINTS] = localPoints.map(p => [p.x, p.y])
    previewMesh.userData[ThreeItemKeys.FORBIDDEN_ZONE_HEIGHT] = finalHeight

    const zone = previewMesh
    previewMesh = null
    resetState()
    // upsert() switches sceneMode to 'translate', which also drives this listener's
    // own $effect cleanup (it only stays active while sceneMode === 'forbiddenZone').
    selectObject(zone)
  }

  const onClick = (e: MouseEvent) => {
    if (!renderer.domElement.parentElement?.contains(e.target as Node) || !scene) return

    if (!closed) {
      const groundPoint = getPointerGroundPoint(e)
      if (!groundPoint) return

      if (isNearFirstPoint(groundPoint)) {
        closePolygon()
        return
      }

      if (!previewOutline) {
        previewOutline = new Line(new BufferGeometry(), previewOutlineMaterial)
        scene.add(previewOutline)
      }

      points = [...points, new Vector2(groundPoint.x, groundPoint.y)]
      updatePreviewOutline()
      return
    }

    finalizeZone()
  }

  function onPointerMove(e: MouseEvent) {
    if (!closed) {
      const groundPoint = getPointerGroundPoint(e)
      if (!groundPoint) return

      // Snap the dangling preview segment onto the first point once within closing
      // range, so the user sees the shape is about to close before they click.
      const cursorPoint = isNearFirstPoint(groundPoint)
        ? new Vector3(points[0].x, points[0].y, 0)
        : groundPoint
      updatePreviewOutline(cursorPoint)
      return
    }

    if (!heightPlane) return

    const raycaster = getPointerRay(e)
    if (!raycaster) return

    const hit = new Vector3()
    if (raycaster.ray.intersectPlane(heightPlane, hit)) {
      currentHeight = Math.max(hit.z, 0)
      updatePreviewMesh(currentHeight)
    }
  }

  const onCancel = () => {
    if (points.length || closed) {
      resetState()
    }
    else {
      $sceneMode = 'default'
    }
  }

  $effect(() => {
    if ($sceneMode === 'forbiddenZone') {
      const mouseEventCallback = useMouseEvents({ onPointerMove, onClick })
      const cancelKeyboardCallback = useKeyboardListener('Escape', {
        onKeyUp: onCancel,
      })
      const undoKeyboardCallback = useKeyboardListener('Backspace', {
        onKeyUp: undoLastPoint,
      })

      return () => {
        mouseEventCallback()
        cancelKeyboardCallback()
        undoKeyboardCallback()
      }
    }
    else {
      resetState()
    }
  })
</script>
